/** Thin API client. Same-origin in prod; via the Vite proxy in dev. Always sends
 *  the session cookie. */

export interface User {
  id: string;
  email: string;
  display_name: string | null;
  role: string;
  division_goal: string | null;
}

export interface Profile {
  user_id: string;
  role: string;
  division_goal: string | null;
  goal_text: string | null;
  current_level: string | null;
  language_pref: string;
  reduce_motion: number;
  dyslexia_spacing: number;
  high_contrast: number;
  theme: string;
  notify_review_due: number;
  notify_assignments: number;
  onboarded_at: string | null;
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });
  if (!res.ok) throw new Error(`api ${path}: ${res.status}`);
  return (await res.json()) as T;
}

export interface DiagnosticItem {
  id: string;
  taskType: string;
  prompt: string;
  options: { id: string; label: string }[];
}

export interface DiagnosticProfile {
  skillScores: { skill: string; perMille: number; items: number }[];
  overall: number;
  misconceptions: { tag: string; count: number }[];
  placementDivision: 'bronze' | 'silver' | 'gold' | 'platinum';
  topBottleneckSkill: string | null;
}

export interface RecommendedAction {
  kind: string;
  reason: string;
  ref?: string;
}

export interface Scenario {
  scenario_id: string;
  division: string;
  topic_families: string[];
  problem_variants: { id: string; text: string; theme: string }[];
  formal_model: { constraints: Record<string, number | string>; objective: string };
  complexity_profile: { expected: string };
  hint_ladder: { title: string; label: string; cost: number }[];
}

export interface ReviewCard {
  id: string;
  skillTags: string[];
  misconceptionTags: string[];
  intervalDays: number;
  dueAt: number;
}

export interface ContestProblem {
  id: string;
  drill: string;
  status: 'unopened' | 'opened' | 'submitted';
  score: number | null;
}
export interface ContestState {
  division: string;
  remainingSec: number;
  locked: boolean;
  problems: ContestProblem[];
}
export interface Postmortem {
  division: string;
  durationSec: number;
  score: number;
  problems: ContestProblem[];
  timeline: { t: number; type: string; problem?: string }[];
}

export interface ClassRow {
  id: string;
  coach_id: string;
  name: string;
  division: string | null;
  join_code: string;
}
export interface RosterEntry {
  user_id: string;
  name: string | null;
  email: string;
  division_goal: string | null;
  readiness: number;
  bottleneck: string | null;
  attempts: number;
}
export interface Assignment {
  id: string;
  title: string;
  kind: string;
  target_ref: string | null;
  difficulty: string;
  due_at: string | null;
  status: string;
  class_name: string;
}

export interface ContentItem {
  scenario_id: string;
  version: string;
  division: string;
  difficulty: number;
  lifecycle: string;
  human_reviewer: string | null;
  open_issues: number;
}
export interface ChecklistItem {
  gate: string;
  pass: boolean;
  detail: string;
}
export interface IssueReport {
  id: string;
  scenario_id: string;
  version: string | null;
  step: string | null;
  category: string;
  severity: string;
  note: string | null;
  status: string;
  created_at: string;
}

export interface HomeData {
  firstRun: boolean;
  onboarded?: boolean;
  division: string;
  readiness?: { score: number; band: string };
  dueCount?: number;
  recommendation?: RecommendedAction[];
  heatmap?: { mastery: number; flag: boolean; label: string }[];
  skills?: { skill: string; topic: string; score: number }[];
}

export const api = {
  me: () => req<{ user: User | null; profile: Profile | null }>('/auth/me'),
  getDiagnostic: () => req<{ items: DiagnosticItem[]; durationSec: number }>('/diagnostic'),
  submitDiagnostic: (responses: { itemId: string; responseId: string }[]) =>
    req<{ profile: DiagnosticProfile }>('/diagnostic/submit', {
      method: 'POST',
      body: JSON.stringify({ responses }),
    }),
  getHome: () => req<HomeData>('/home'),
  submitDrill: (kind: string, response: unknown) =>
    req<{ grade: unknown }>('/drill/result', {
      method: 'POST',
      body: JSON.stringify({ kind, response }),
    }),
  getScenario: (id: string) =>
    req<{ version: string; bundle: Scenario }>(`/content/scenario/${id}`),
  seedContent: () => req<{ ok: boolean }>('/content/seed', { method: 'POST' }),
  getReview: () => req<{ cards: ReviewCard[]; count: number }>('/review'),
  gradeReview: (id: string, grade: string) =>
    req<{ ok: boolean; intervalDays: number }>('/review/grade', {
      method: 'POST',
      body: JSON.stringify({ id, grade }),
    }),
  getMastery: () =>
    req<{
      division: string;
      readiness: { score: number; band: string };
      skills: { skill: string; score: number; measured: boolean }[];
      heatmap: { mastery: number; flag: boolean; label: string }[];
    }>('/progress/mastery'),
  getWorkout: () => req<{ slots: string[]; estimatedMin: number }>('/progress/workout'),
  contestStart: (division: string, duration: string) =>
    req<{ ok: boolean; remainingSec: number }>('/contest/start', {
      method: 'POST',
      body: JSON.stringify({ division, duration }),
    }),
  contestState: () => req<ContestState>('/contest/state'),
  contestEvent: (type: string, problem?: string) =>
    req<{ ok: boolean }>('/contest/event', {
      method: 'POST',
      body: JSON.stringify({ type, problem }),
    }),
  contestSubmit: (problem: string, score: number) =>
    req<{ ok: boolean }>('/contest/submit', {
      method: 'POST',
      body: JSON.stringify({ problem, score }),
    }),
  contestLock: () => req<{ ok: boolean }>('/contest/lock', { method: 'POST' }),
  contestPostmortem: () => req<Postmortem>('/contest/postmortem'),
  getRepair: () =>
    req<{ weakSkill: string; weakTopic: string | null; slots: string[] }>('/progress/repair'),
  recordAttempt: (payload: {
    scenarioId: string;
    contentVersion: string;
    mode: string;
    score: number;
    topic?: string;
    skill?: string;
    firstFailStage?: string | null;
  }) =>
    req<{ ok: boolean; attemptId: string }>('/attempts', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  logout: () => req<{ ok: boolean }>('/auth/logout', { method: 'POST' }),
  patchProfile: (patch: Partial<Profile>) =>
    req<{ ok: boolean; profile: Profile }>('/account/profile', {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  deleteAccount: () => req<{ ok: boolean }>('/account', { method: 'DELETE' }),
  /** Full-page navigation so the OAuth redirects work. */
  signInWithGoogle: (redirect = '/app') => {
    window.location.href = `/api/auth/google/start?redirect=${encodeURIComponent(
      `${window.location.origin}${redirect}`,
    )}`;
  },
  /** Trigger a browser download of the export JSON. */
  exportData: () => {
    window.location.href = '/api/account/export';
  },
  /** Dev-only sign-in (no Google). Returns whether the account is new. */
  devLogin: () => req<{ ok: boolean; isNew: boolean }>('/auth/dev-login', { method: 'POST' }),

  // ---- Coach ----
  coachCreateClass: (name: string, division: string) =>
    req<{ class: ClassRow }>('/coach/classes', {
      method: 'POST',
      body: JSON.stringify({ name, division }),
    }),
  coachClasses: () => req<{ classes: ClassRow[] }>('/coach/classes'),
  coachRoster: (classId: string) =>
    req<{ class: ClassRow; roster: RosterEntry[] }>(`/coach/classes/${classId}/roster`),
  coachCohorts: (classId: string) =>
    req<{ cohorts: { skill: string; students: string[] }[] }>(`/coach/classes/${classId}/cohorts`),
  coachCreateAssignment: (
    classId: string,
    body: { title: string; kind: string; targetRef?: string; difficulty?: string },
  ) =>
    req<{ ok: boolean }>(`/coach/classes/${classId}/assignments`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  coachStudent: (id: string) =>
    req<{
      student: { id: string; name: string | null; email: string };
      readiness: { score: number; band: string };
      skills: { skill: string; topic: string; score: number }[];
    }>(`/coach/students/${id}`),

  // ---- Admin / content-ops ----
  devRole: (role: string) =>
    req<{ ok: boolean; role: string }>('/auth/dev-role', {
      method: 'POST',
      body: JSON.stringify({ role }),
    }),
  adminContent: () => req<{ items: ContentItem[] }>('/admin/content'),
  adminScenario: (id: string, version: string) =>
    req<{
      bundle: unknown;
      validation: { ok: boolean; errors: { code: string; path: string }[] };
      checklist: ChecklistItem[];
    }>(`/admin/content/${id}/${version}`),
  adminRollback: (id: string, toVersion: string) =>
    req<{ ok: boolean }>(`/admin/content/${id}/rollback`, {
      method: 'POST',
      body: JSON.stringify({ toVersion }),
    }),
  adminIssues: (status = 'open') =>
    req<{ issues: IssueReport[] }>(`/admin/issues?status=${status}`),
  adminResolve: (id: string, resolution: string) =>
    req<{ ok: boolean }>(`/admin/issues/${id}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ resolution }),
    }),
  adminAnalytics: () =>
    req<{ byLifecycle: { lifecycle: string; n: number }[]; openIssues: number }>(
      '/admin/analytics',
    ),
  reportIssue: (scenarioId: string, body: { step?: string; category?: string; note?: string }) =>
    req<{ ok: boolean }>('/issues', {
      method: 'POST',
      body: JSON.stringify({ scenarioId, ...body }),
    }),

  // ---- Student classroom ----
  joinClass: (code: string) =>
    req<{ ok: boolean }>('/classroom/join', { method: 'POST', body: JSON.stringify({ code }) }),
  getAssignments: () => req<{ assignments: Assignment[] }>('/classroom/assignments'),
  completeAssignment: (id: string, score = 1000) =>
    req<{ ok: boolean }>(`/classroom/assignments/${id}/complete`, {
      method: 'POST',
      body: JSON.stringify({ score }),
    }),
};
